import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Package, Printer, ChevronDown, ChevronUp, Camera, X, Trash2, RefreshCw, FileText, Plus, Edit2, Upload, Download } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { useSettings } from '../../context/SettingsContext';
import { packingLabelInnerHtml, packingLabelPrintDoc, PACKING_LABEL_CSS, getBoxSubLabels } from '../../utils/packingLabel';
import { buildImageUrl } from '../../utils/imageUrl';
import BoxModal from '../../components/PackingBoxModal';
import PackingChallanReport from './PackingChallanReport';
import DeliveryBoxesReport from './DeliveryBoxesReport';
import ProjectSelect from '../../components/ProjectSelect';

// Small square thumbnail URL for exports (Cloudinary transform, jpg for Word compatibility)
function exportThumbUrl(imagePath, size = 120) {
  const url = buildImageUrl(imagePath);
  if (!url) return '';
  return url.includes('/image/upload/')
    ? url.replace('/image/upload/', `/image/upload/w_${size},h_${size},c_fill,q_auto,f_jpg/`)
    : url;
}

const safeDate = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy, hh:mm a') : '-'; } catch { return '-'; } };

// A box printed with label copies (A, B…) is one physical box per label
const physicalBoxes = list => list.reduce((s, b) => s + Math.max(1, Math.min(26, parseInt(b.copies) || 1)), 0);

function boxDisplayNumber(box) {
  return box.sub_label ? (box.box_number + ' (' + box.sub_label + ')') : box.box_number;
}


function BoxLabel({ box, onClose, onCopiesSaved }) {
  const baseNum = box.box_number;
  const [copies, setCopies] = useState(Math.max(1, Math.min(26, parseInt(box.copies) || 1)));

  // Persist copies to backend whenever it changes, so Export PDF shows
  // the sub-boxes (A, B, C...) even after the modal is closed / page reloaded.
  const changeCopies = (next) => {
    const v = Math.max(1, Math.min(26, typeof next === 'function' ? next(copies) : next));
    setCopies(v);
    api.put('/packing/boxes/' + box.id + '/copies', { copies: v })
      .then(() => { onCopiesSaved && onCopiesSaved(box.id, v); })
      .catch(() => toast.error('Failed to save copies'));
  };

  // 1 copy = original label; 2+ = sub-labels -A, -B, -C...
  const SUB_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const getLabels = () => {
    const n = Math.max(1, Math.min(26, copies));
    if (n === 1) return [baseNum];
    return Array.from({ length: n }, (_, i) => baseNum + '-' + SUB_LABELS[i]);
  };

  const handlePrint = () => {
    const labels = getLabels();
    const allLabelsHtml = labels.map(dn => packingLabelInnerHtml(box, dn)).join('\n');
    const win = window.open('', '_blank');
    win.document.write(packingLabelPrintDoc(allLabelsHtml));
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  const labels = getLabels();
  const previewLabel = labels[0];
  // Preview uses the EXACT same HTML that prints.
  const previewHtml = `<style>${PACKING_LABEL_CSS}</style>${packingLabelInnerHtml(box, previewLabel)}`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h3 className="font-bold flex items-center gap-2"><Printer className="w-5 h-5 text-blue-600" /> Label Preview — {baseNum}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {/* Copies input */}
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-amber-800 mb-2">How many copies? (Sub-labels A, B, C... will be added)</p>
            <div className="flex items-center gap-3">
              <button onClick={() => changeCopies(c => c - 1)}
                className="w-8 h-8 bg-white border border-amber-300 rounded-lg font-bold text-amber-700 hover:bg-amber-100 transition-colors flex items-center justify-center text-lg">−</button>
              <input
                type="number" min={1} max={26} value={copies}
                onChange={e => changeCopies(parseInt(e.target.value) || 1)}
                className="w-16 text-center border border-amber-300 rounded-lg py-1.5 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <button onClick={() => changeCopies(c => c + 1)}
                className="w-8 h-8 bg-white border border-amber-300 rounded-lg font-bold text-amber-700 hover:bg-amber-100 transition-colors flex items-center justify-center text-lg">+</button>
              <div className="flex-1">
                {copies === 1
                  ? <span className="text-xs text-gray-500">Single label: <strong>{baseNum}</strong></span>
                  : <span className="text-xs text-gray-500">
                      Labels: <strong className="text-amber-700">{labels.slice(0, 4).join('  ')} {copies > 4 ? '...' + labels[copies-1] : ''}</strong>
                    </span>
                }
              </div>
            </div>
          </div>

          {/* Preview of first label */}
          <p className="text-xs text-gray-400 mb-2 text-center">Preview (first label)</p>
          <div className="flex justify-center">
            <div style={{ transform: 'scale(0.95)', transformOrigin: 'top center' }}
                 dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
        <div className="p-4 border-t flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm">Close</button>
          <button onClick={handlePrint} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2">
            <Printer className="w-4 h-4" /> Print {copies} Label{copies > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

function BoxPhotoGallery({ box }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const loadPhotos = async () => {
    if (loaded) { setExpanded(!expanded); return; }
    setLoading(true);
    try { const r = await api.get('/packing/boxes/' + box.id + '/photos'); setPhotos(r.data); setLoaded(true); setExpanded(true); }
    catch { toast.error('Failed to load photos'); } finally { setLoading(false); }
  };
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const form = new FormData();
    files.forEach(f => form.append('photos', f));
    try {
      const r = await api.post('/packing/boxes/' + box.id + '/photos', form);
      toast.success(files.length + ' photo(s) uploaded ✅');
      if (r.data.photos?.length > 0) setPhotos(r.data.photos);
      else { const r2 = await api.get('/packing/boxes/' + box.id + '/photos'); setPhotos(r2.data); }
      setLoaded(true); setExpanded(true);
    } catch (err) { toast.error(err?.response?.data?.message || 'Upload fail'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete('/packing/boxes/' + box.id + '/photos/' + photoId); setPhotos(p => p.filter(ph => ph.id !== photoId)); toast.success('Deleted'); }
    catch { toast.error('Failed to delete'); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-center justify-between">
        <button onClick={loadPhotos} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
          <Camera className="w-4 h-4" />
          {loading ? 'Load ho raha...' : ('Photos dekho' + (loaded ? ' (' + photos.length + ')' : ''))}
          {loaded && (expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
        </button>
        <button onClick={() => fileRef.current.click()} disabled={uploading}
          className="flex items-center gap-1 text-xs bg-blue-500 hover:bg-blue-600 text-white px-2.5 py-1 rounded-lg font-medium disabled:opacity-50">
          <Upload className="w-3 h-3" /> {uploading ? 'Upload...' : 'Upload'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
      </div>
      {expanded && (
        <div className="mt-3">
          {photos.length === 0 ? <p className="text-xs text-gray-400 bg-gray-50 rounded-xl p-3 text-center">No photos yet</p> : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {photos.map(photo => (
                <div key={photo.id} className="relative group">
                  <img src={photo.image_url} alt="packing" onClick={() => setLightbox(photo)}
                    className="w-full h-20 object-cover rounded-xl border border-gray-200 cursor-pointer group-hover:ring-2 group-hover:ring-blue-400 transition-all" />
                  <button onClick={() => handleDelete(photo.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <img src={lightbox.image_url} alt="full" className="w-full rounded-2xl shadow-2xl max-h-[80vh] object-contain" />
            <button onClick={() => setLightbox(null)} className="absolute top-3 right-3 bg-white/20 hover:bg-white/40 text-white rounded-full p-1.5"><X className="w-5 h-5" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function BoxCard({ box, onDelete, onPrint, onEdit }) {
  const [showItems, setShowItems] = useState(false);
  const totalQty = box.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;
  const displayNum = boxDisplayNumber(box);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold text-gray-900 tracking-wide">{displayNum}</span>
              {box.proto_code && <span className="text-xs font-mono bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-bold">📷 {box.proto_code}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {box.project_name && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">📁 {box.project_name}</span>}
              {box.item_name && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">📦 {box.item_name}</span>}
              {box.sale_challan_number && <span className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full font-semibold">🧾 {box.sale_challan_number}</span>}
            </div>
            <div className="flex gap-3 mt-1.5 text-xs text-gray-400 flex-wrap">
              <span>👤 {box.created_by_name}</span>
              <span>🕐 {safeDate(box.created_at)}</span>
              <span>{box.items?.length || 0} items • {totalQty} units</span>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => onEdit(box)} className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl transition-colors"><Edit2 className="w-4 h-4" /></button>
            <button onClick={() => onDelete(box.id)} className="p-2 border border-red-100 text-red-400 hover:bg-red-50 rounded-xl transition-colors"><Trash2 className="w-4 h-4" /></button>
            <button onClick={() => onPrint(box)} className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors"><Printer className="w-4 h-4" /></button>
          </div>
        </div>
        {box.items?.length > 0 && (
          <>
            <button onClick={() => setShowItems(!showItems)} className="mt-2 text-xs text-gray-400 flex items-center gap-1 hover:text-gray-600">
              {showItems ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showItems ? 'Contents chhupao' : ('Contents dekho (' + box.items.length + ' items)')}
            </button>
            {showItems && (
              <div className="mt-2 space-y-1">
                {box.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-xs bg-gray-50 rounded-lg px-3 py-1.5">
                    <span className="text-gray-700">{i + 1}. {item.item_name}</span>
                    <span className="font-bold text-gray-600">{item.quantity} {item.unit}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {box.notes && <div className="mt-2 text-xs text-gray-500 bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-1.5">📝 {box.notes}</div>}
        <BoxPhotoGallery box={box} />
      </div>
    </div>
  );
}

export default function AdminPackingPage() {
  const { settings } = useSettings();
  const [boxes, setBoxes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [projectFilter, setProjectFilter] = useState('');
  const [search, setSearch] = useState('');
  const [printBox, setPrintBox] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editBox, setEditBox] = useState(null);
  const [nextBoxNum, setNextBoxNum] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('boxes'); // 'boxes' | 'challan' | 'delivery'

  // Reset to first page whenever the filter or search changes
  useEffect(() => { setPage(1); }, [projectFilter, search]);

  const load = async (soft) => {
    if (soft) setRefreshing(true); else setLoading(true);
    try {
      const [bRes, pRes, nRes] = await Promise.all([
        api.get('/packing/boxes'),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/packing/next-box-number').catch(() => ({ data: { box_number: '' } })),
      ]);
      setBoxes(bRes.data); setProjects(pRes.data); setNextBoxNum(nRes.data.box_number);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this box?')) return;
    try { await api.delete('/packing/boxes/' + id); toast.success('Deleted'); setBoxes(b => b.filter(x => x.id !== id)); }
    catch { toast.error('Failed to delete'); }
  };

  const filtered = boxes.filter(box => {
    const mp = !projectFilter || String(box.project_id) === projectFilter;
    const ms = !search || boxDisplayNumber(box).toLowerCase().includes(search.toLowerCase()) || (box.proto_code||'').toLowerCase().includes(search.toLowerCase()) || (box.created_by_name||'').toLowerCase().includes(search.toLowerCase()) || (box.project_name||'').toLowerCase().includes(search.toLowerCase());
    return mp && ms;
  });

  // ── Pagination: 50 boxes per page (display only — PDF/Word exports still cover ALL filtered boxes) ──
  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages); // auto-clamp if deletes/filters shrink the list
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const exportPDF = async () => {
    if (!filtered.length) return toast.error('No boxes to export');
    const now = new Date();
    const today = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' });
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    const dateTime = today + ' ' + timeStr;
    const SUB = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const esc = (v) => String(v == null || v === '' ? '-' : v).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const copiesOf = (b) => Math.max(1, Math.min(26, parseInt(b.copies) || 1));

    // Group boxes same as Word export
    const groups = []; const gmap = new Map();
    filtered.forEach(box => {
      const key = [box.project_id, box.main_item || box.item_name, box.photo_code || box.item_proto_code].join('|');
      if (!gmap.has(key)) {
        const g = { projectName: box.project_name || box.client_name || '-', projCode: box.proj_code || '-',
                    itemName: box.main_item || box.item_name || '-', protoCode: box.photo_code || box.item_proto_code || '-', boxes: [] };
        gmap.set(key, g); groups.push(g);
      }
      const g = gmap.get(key);
      const c = copiesOf(box);
      const nums = c > 1 ? Array.from({ length: c }, (_, i) => box.box_number + '-' + SUB[i]) : [box.box_number];
      const items = box.items?.length ? box.items : [{ item_name: box.item_name || '-', quantity: box.item_quantity ?? '-', unit: box.item_unit || 'PCS' }];
      const img = exportThumbUrl(box.box_image || box.item_image);
      nums.forEach(dn => g.boxes.push({ dn, items, img }));
    });

    const totalBoxes = groups.reduce((s, g) => s + g.boxes.length, 0);

    // Fetch all unique images as data URIs so they render in print/PDF without CORS issues
    const uniqueImgUrls = [...new Set(groups.flatMap(g => g.boxes.map(b => b.img)).filter(Boolean))];
    const dataUriByUrl = new Map();
    if (uniqueImgUrls.length) {
      const results = await Promise.allSettled(uniqueImgUrls.map(async (url) => {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error('fetch failed');
        const blob = await resp.blob();
        const dataUri = await new Promise((resolve, reject) => {
          const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.onerror = reject; fr.readAsDataURL(blob);
        });
        return { url, dataUri };
      }));
      results.forEach(r => { if (r.status === 'fulfilled') dataUriByUrl.set(r.value.url, r.value.dataUri); });
    }
    const imgSrcFor = (url) => dataUriByUrl.get(url) || url;

    // Column widths in px (96dpi screen) matching Word pt values:
    // sno=37, proj=187, code=83, item=133, proto=83, boxno=107, img=69, desc=160, qty=48, uom=48 → total=955px
    // A4 landscape at 96dpi = ~1122px usable (297mm); with 15mm*2 margins = 267mm ≈ 1016px — fits perfectly
    const COL = { sno:37, proj:187, code:83, item:133, proto:83, boxno:107, img:69, desc:160, qty:48, uom:48 };
    const tableW = Object.values(COL).reduce((a,b)=>a+b,0);
    const th = (w, extra='') => `border:1px solid #999;padding:4px 3px;text-align:center;font-weight:700;font-size:8.5pt;background:#F2F2F2;vertical-align:middle;width:${w}px;${extra}`;
    const td = (w, extra='') => `border:1px solid #999;padding:4px 3px;font-size:8.5pt;vertical-align:middle;width:${w}px;${extra}`;

    let rows = '', sr = 1;
    groups.forEach(g => {
      const groupRows = g.boxes.reduce((s, b) => s + b.items.length, 0);
      let firstOfGroup = true;
      g.boxes.forEach(b => {
        let firstOfBox = true;
        b.items.forEach(it => {
          rows += '<tr>';
          if (firstOfGroup) {
            rows += `<td rowspan="${groupRows}" style="${td(COL.sno,'text-align:center;font-weight:700;')}">${sr}</td>`
                  + `<td rowspan="${groupRows}" style="${td(COL.proj,'font-weight:700;')}">${esc(g.projectName)}</td>`
                  + `<td rowspan="${groupRows}" style="${td(COL.code)}">${esc(g.projCode)}</td>`
                  + `<td rowspan="${groupRows}" style="${td(COL.item)}">${esc(g.itemName)}</td>`
                  + `<td rowspan="${groupRows}" style="${td(COL.proto,'font-family:monospace;')}">${esc(g.protoCode)}</td>`;
            firstOfGroup = false;
          }
          if (firstOfBox) {
            rows += `<td rowspan="${b.items.length}" style="${td(COL.boxno,'font-weight:700;')}">${esc(b.dn)}</td>`
                  + `<td rowspan="${b.items.length}" style="${td(COL.img,'text-align:center;')}">`
                  + (b.img ? `<img src="${esc(imgSrcFor(b.img))}" style="width:44px;height:44px;object-fit:cover;border-radius:3px;display:block;margin:0 auto;" />` : '-')
                  + '</td>';
            firstOfBox = false;
          }
          rows += `<td style="${td(COL.desc)}">${esc(it.item_name)}</td>`
                + `<td style="${td(COL.qty,'text-align:right;font-weight:600;')}">${esc(it.quantity)}</td>`
                + `<td style="${td(COL.uom)}">${esc(it.unit || 'PCS')}</td></tr>`;
        });
      });
      sr++;
    });

    const companyName = esc(settings.company_name || 'Workshop');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Packing Report</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,sans-serif;padding:20px}
      table{border-collapse:collapse;width:${tableW}px;table-layout:fixed;}
      @media print{
        @page{size:A4 landscape;margin:12mm 15mm;}
        body{padding:0;}
        h2{font-size:13pt;}
      }
    </style></head><body>
    <h2 style="margin-bottom:4px;font-size:14pt;">${companyName} — Packing Report</h2>
    <p style="font-size:9pt;color:#555;margin-bottom:12px;">Date: ${esc(dateTime)} &nbsp;|&nbsp; Total Boxes: ${totalBoxes}</p>
    <table>
      <thead><tr>
        <th style="${th(COL.sno)}">S.NO</th>
        <th style="${th(COL.proj)}">PROJECT NAME</th>
        <th style="${th(COL.code)}">PROJECT CODE</th>
        <th style="${th(COL.item)}">ITEM NAME</th>
        <th style="${th(COL.proto)}">PROTO CODE</th>
        <th style="${th(COL.boxno)}">BOX NO</th>
        <th style="${th(COL.img)}">IMAGE</th>
        <th style="${th(COL.desc)}">ITEM DESCRIPTION</th>
        <th style="${th(COL.qty)}">QTY</th>
        <th style="${th(COL.uom)}">UOM</th>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr>
        <td colspan="5" style="border:1px solid #999;padding:5px;text-align:center;font-weight:700;font-size:8.5pt;">TOTAL QTY OF BOXES</td>
        <td style="border:1px solid #999;padding:5px;text-align:center;font-weight:700;font-size:8.5pt;">${totalBoxes}</td>
        <td colspan="4" style="border:1px solid #999;"></td>
      </tr></tfoot>
    </table>
    </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    // Images already embedded as data URIs — no network wait needed, just a brief layout settle
    setTimeout(() => { try { win.print(); } catch {} }, 800);
  };

  // ── Export Word (.doc via HTML-in-Word trick — opens in MS Word / LibreOffice) ──
  // Images are embedded as data URIs directly in the HTML so the file is fully
  // self-contained and images show on every machine without internet access.
  const exportWord = async () => {
    if (!filtered.length) return toast.error('No boxes to export');
    const now = new Date();
    const today = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' });
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    const dateTime = today + ' ' + timeStr;
    const SUB = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const esc = (v) => String(v == null || v === '' ? '-' : v).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const copiesOf = (b) => Math.max(1, Math.min(26, parseInt(b.copies) || 1));

    // Build same groups as PDF
    const groups = []; const gmap = new Map();
    filtered.forEach(box => {
      const key = [box.project_id, box.main_item || box.item_name, box.photo_code || box.item_proto_code].join('|');
      if (!gmap.has(key)) {
        const g = { projectName: box.project_name || box.client_name || '-', projCode: box.proj_code || '-',
                    itemName: box.main_item || box.item_name || '-', protoCode: box.photo_code || box.item_proto_code || '-', boxes: [] };
        gmap.set(key, g); groups.push(g);
      }
      const g = gmap.get(key);
      const c = copiesOf(box);
      const nums = c > 1 ? Array.from({ length: c }, (_, i) => box.box_number + '-' + SUB[i]) : [box.box_number];
      const items = box.items?.length ? box.items : [{ item_name: box.item_name || '-', quantity: box.item_quantity ?? '-', unit: box.item_unit || 'PCS' }];
      const img = exportThumbUrl(box.box_image || box.item_image); // same priority as print label
      nums.forEach(dn => g.boxes.push({ dn, items, img }));
    });

    const totalBoxes = groups.reduce((s, g) => s + g.boxes.length, 0);
    const companyName = esc(settings.company_name || 'Workshop');

    // ── Fetch each unique image once and convert to base64 data URI ──
    // Embedded directly in HTML src — no MHT, no local file paths.
    // Falls back to the direct https URL if fetch fails.
    const uniqueImgUrls = [...new Set(groups.flatMap(g => g.boxes.map(b => b.img)).filter(Boolean))];
    const dataUriByUrl = new Map(); // url -> data URI (or original url as fallback)
    if (uniqueImgUrls.length) {
      const results = await Promise.allSettled(uniqueImgUrls.map(async (url) => {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error('fetch failed');
        const blob = await resp.blob();
        const dataUri = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
        return { url, dataUri };
      }));
      results.forEach(r => {
        if (r.status === 'fulfilled') {
          dataUriByUrl.set(r.value.url, r.value.dataUri);
        }
      });
    }
    // Use embedded data URI so images work on any machine; fallback to https URL
    const imgSrcFor = (url) => dataUriByUrl.get(url) || url;

    // Table rows — Word doesn't support rowspan well, so we flatten (repeat group cells per row)
    // and use merged cells via mso-table tricks with vMerge
    // Strategy: build flat rows, mark first-of-group / first-of-box
    const flatRows = [];
    let sr = 1;
    groups.forEach(g => {
      const groupRowCount = g.boxes.reduce((s, b) => s + b.items.length, 0);
      let groupFirstRow = true;
      g.boxes.forEach(b => {
        let boxFirstRow = true;
        b.items.forEach(it => {
          flatRows.push({
            sr: groupFirstRow ? sr : null,
            projectName: groupFirstRow ? g.projectName : null,
            projCode: groupFirstRow ? g.projCode : null,
            itemName: groupFirstRow ? g.itemName : null,
            protoCode: groupFirstRow ? g.protoCode : null,
            groupRowCount,
            boxNum: boxFirstRow ? b.dn : null,
            boxImg: boxFirstRow ? (b.img || '') : null,
            boxRowCount: b.items.length,
            itemDesc: it.item_name,
            qty: it.quantity,
            unit: it.unit || 'PCS',
            groupFirstRow,
            boxFirstRow,
          });
          groupFirstRow = false;
          boxFirstRow = false;
        });
      });
      sr++;
    });

    // Word-compatible HTML using <col> tags — the only reliable way to set column
    // widths in Word HTML. mso-width-source:userset locks the width so Word won't auto-resize.
    // A4 landscape usable = 297mm - 30mm margins = 267mm = 15120 twips
    // 1pt = 20 twips. Column widths in pt summing to ≤756pt:
    const COL = { sno:26, proj:138, code:60, item:98, proto:60, boxno:78, img:50, desc:118, qty:34, uom:34 };
    // total = 696pt — leaves ~60pt slack so Word doesn't overflow on any printer
    const tableWidth = Object.values(COL).reduce((a,b)=>a+b,0);
    const thStyle = `border:1pt solid #999;padding:3pt 2pt;text-align:center;font-weight:bold;font-size:8pt;background:#F2F2F2;vertical-align:middle;`;
    const tdBase  = `border:1pt solid #999;padding:3pt 2pt;font-size:8pt;vertical-align:middle;`;

    // <col> group — Word reads these to set column widths; inline width on td is a fallback
    const colGroup = `<colgroup>
      <col style="width:${COL.sno}pt;mso-width-source:userset;"/>
      <col style="width:${COL.proj}pt;mso-width-source:userset;"/>
      <col style="width:${COL.code}pt;mso-width-source:userset;"/>
      <col style="width:${COL.item}pt;mso-width-source:userset;"/>
      <col style="width:${COL.proto}pt;mso-width-source:userset;"/>
      <col style="width:${COL.boxno}pt;mso-width-source:userset;"/>
      <col style="width:${COL.img}pt;mso-width-source:userset;"/>
      <col style="width:${COL.desc}pt;mso-width-source:userset;"/>
      <col style="width:${COL.qty}pt;mso-width-source:userset;"/>
      <col style="width:${COL.uom}pt;mso-width-source:userset;"/>
    </colgroup>`;

    let rows = '';
    flatRows.forEach(r => {
      rows += '<tr>';
      if (r.groupFirstRow) {
        rows += `<td rowspan="${r.groupRowCount}" style="${tdBase}width:${COL.sno}pt;text-align:center;font-weight:bold;">${esc(r.sr)}</td>`;
        rows += `<td rowspan="${r.groupRowCount}" style="${tdBase}width:${COL.proj}pt;font-weight:bold;">${esc(r.projectName)}</td>`;
        rows += `<td rowspan="${r.groupRowCount}" style="${tdBase}width:${COL.code}pt;">${esc(r.projCode)}</td>`;
        rows += `<td rowspan="${r.groupRowCount}" style="${tdBase}width:${COL.item}pt;">${esc(r.itemName)}</td>`;
        rows += `<td rowspan="${r.groupRowCount}" style="${tdBase}width:${COL.proto}pt;font-family:Courier New,monospace;">${esc(r.protoCode)}</td>`;
      }
      if (r.boxFirstRow) {
        rows += `<td rowspan="${r.boxRowCount}" style="${tdBase}width:${COL.boxno}pt;font-weight:bold;">${esc(r.boxNum)}</td>`;
        rows += `<td rowspan="${r.boxRowCount}" style="${tdBase}width:${COL.img}pt;text-align:center;">`
              + (r.boxImg ? `<img src="${esc(imgSrcFor(r.boxImg))}" width="44" height="44" style="width:33pt;height:33pt;object-fit:cover;" />` : '-')
              + '</td>';
      }
      rows += `<td style="${tdBase}width:${COL.desc}pt;">${esc(r.itemDesc)}</td>`;
      rows += `<td style="${tdBase}width:${COL.qty}pt;text-align:right;font-weight:600;">${esc(r.qty)}</td>`;
      rows += `<td style="${tdBase}width:${COL.uom}pt;">${esc(r.unit)}</td>`;
      rows += '</tr>';
    });

    const wordHtml = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:w="urn:schemas-microsoft-com:office:word"
  xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8"/>
<meta name=ProgId content=Word.Document>
<meta name=Generator content="Microsoft Word 15">
<!--[if gte mso 9]>
<xml><w:WordDocument><w:View>Normal</w:View><w:Zoom>100</w:Zoom>
<w:DoNotOptimizeForBrowser/></w:WordDocument></xml>
<![endif]-->
<style>
  @page { size:A4 landscape; margin:12mm 15mm; mso-page-orientation:landscape; }
  body { font-family:Arial,sans-serif; font-size:10pt; }
  table { border-collapse:collapse; width:${tableWidth}pt; mso-table-layout-alt:fixed; mso-table-lspace:0pt; mso-table-rspace:0pt; }
  h2 { font-size:13pt; margin-bottom:4pt; }
  p.meta { font-size:8.5pt; color:#555; margin-bottom:8pt; }
</style>
</head>
<body>
<h2>${companyName} — Packing Report</h2>
<p class="meta">Date: ${esc(dateTime)} &nbsp;|&nbsp; Total Boxes: ${totalBoxes}</p>
<table>
  ${colGroup}
  <thead>
    <tr>
      <th style="${thStyle}">S.NO</th>
      <th style="${thStyle}">PROJECT NAME</th>
      <th style="${thStyle}">PROJECT CODE</th>
      <th style="${thStyle}">ITEM NAME</th>
      <th style="${thStyle}">PROTO CODE</th>
      <th style="${thStyle}">BOX NO</th>
      <th style="${thStyle}">IMAGE</th>
      <th style="${thStyle}">ITEM DESCRIPTION</th>
      <th style="${thStyle}">QTY</th>
      <th style="${thStyle}">UOM</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
  <tfoot>
    <tr>
      <td colspan="5" style="${tdBase}text-align:center;font-weight:bold;">TOTAL QTY OF BOXES</td>
      <td style="${tdBase}text-align:center;font-weight:bold;">${totalBoxes}</td>
      <td colspan="4" style="${tdBase}"></td>
    </tr>
  </tfoot>
</table>
</body></html>`;

    // Self-contained HTML .doc — images are data URIs embedded directly in HTML,
    // so the file opens correctly on any machine without internet or local files.
    const blob = new Blob(['\ufeff' + wordHtml], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fileName = `Packing-Report-${now.toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric', timeZone:'Asia/Kolkata' }).replace(/\//g,'-')}.doc`;
    a.href = url; a.download = fileName; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2"><Package className="w-6 h-6 text-purple-600" /> Packing Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tab === 'boxes' ? `${physicalBoxes(filtered)} boxes${physicalBoxes(filtered) !== filtered.length ? ` (${filtered.length} entries)` : ''}` : tab === 'delivery' ? 'Boxes delivered on challans vs pending' : 'Packed boxes on Sale Challans vs pending'}</p>
        </div>
        {tab === 'boxes' && <div className="flex gap-2">
          <button onClick={() => load(true)} disabled={refreshing} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-sm transition-colors">
            <RefreshCw className={"w-4 h-4" + (refreshing ? " animate-spin" : "")} /> Refresh
          </button>
          <button onClick={() => { setEditBox(null); setShowCreateModal(true); }} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-xl text-sm font-bold transition-colors">
            <Plus className="w-4 h-4" /> New Box
          </button>
          <button onClick={exportPDF} className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl text-sm font-semibold transition-colors">
            <FileText className="w-4 h-4" /> Export PDF
          </button>
          <button onClick={exportWord} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl text-sm font-semibold transition-colors">
            <Download className="w-4 h-4" /> Export Word
          </button>
        </div>}
      </div>
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl w-fit max-w-full">
        {[['boxes', 'Boxes'], ['challan', 'Sale Challan vs Packing'], ['delivery', 'Delivery Challan vs Boxes']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 sm:px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === k ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'challan' ? <PackingChallanReport projects={projects} /> : tab === 'delivery' ? <DeliveryBoxesReport projects={projects} /> : <>
      <div className="flex gap-3 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Box number, proto code, project..." className="flex-1 min-w-40 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
        <ProjectSelect projects={projects} value={projectFilter} onChange={v => setProjectFilter(v)}
              placeholder="Sab Projects" format="name-code" className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[{l:'Total Boxes',v:physicalBoxes(filtered),c:'text-purple-700',bg:'bg-purple-50',b:'border-purple-200'},{l:'Total Items',v:filtered.reduce((s,b)=>s+(b.items?.length||0),0),c:'text-blue-700',bg:'bg-blue-50',b:'border-blue-200'},{l:'Workers',v:[...new Set(filtered.map(b=>b.created_by_name))].length,c:'text-green-700',bg:'bg-green-50',b:'border-green-200'}].map(s=>(
          <div key={s.l} className={s.bg+' '+s.b+' border rounded-2xl p-3 sm:p-4 text-center'}>
            <p className={'text-xl sm:text-2xl font-bold '+s.c}>{s.v}</p>
            <p className={'text-xs font-semibold mt-0.5 '+s.c+' opacity-80'}>{s.l}</p>
          </div>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100"><Package className="w-12 h-12 mx-auto mb-3 text-gray-200" /><p className="font-semibold text-gray-400">No boxes found</p></div>
      ) : (
        <>
          <div className="space-y-3">{paged.map(box => <BoxCard key={box.id} box={box} onDelete={handleDelete} onPrint={setPrintBox} onEdit={(b) => { setEditBox(b); setShowCreateModal(true); }} />)}</div>
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 bg-white rounded-2xl border border-gray-100 px-4 py-3">
              <p className="text-xs text-gray-500 font-medium">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length} entries
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                  className="px-3 py-1.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Previous
                </button>
                <span className="text-sm font-semibold text-gray-700 px-2">Page {safePage} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                  className="px-3 py-1.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
      </>}
      {printBox && <BoxLabel box={printBox} onClose={() => setPrintBox(null)}
        onCopiesSaved={(id, n) => setBoxes(bs => bs.map(b => b.id === id ? { ...b, copies: n } : b))} />}
      {showCreateModal && (
        <BoxModal projects={projects} editBox={editBox} nextBoxNum={nextBoxNum}
          onClose={() => { setShowCreateModal(false); setEditBox(null); }}
          onSaved={async (freshBox) => {
            setShowCreateModal(false);
            setEditBox(null);
            if (freshBox) {
              setBoxes(prev => prev.map(b => b.id === freshBox.id ? freshBox : b));
              if (printBox && printBox.id === freshBox.id) setPrintBox(freshBox);
            } else {
              const res = await api.get('/packing/boxes').catch(() => null);
              if (res) setBoxes(res.data);
            }
          }}
        />
      )}
    </div>
  );
}
